import React, { useState, useEffect } from "react";
import { t } from "../i18n";
import "./TransitionEditor.css";

/**
 * TransitionEditor - Component for editing transitions between segments
 *
 * Props:
 * - fromSegment: The segment before the transition
 * - toSegment: The segment after the transition
 * - onSave: Callback when transition is saved
 * - onClose: Callback to close the editor
 */

function getTransitionTypes() {
  return [
    { value: "fade", label: "Fade", description: t("transition.fade") },
    { value: "crossfade", label: "Crossfade", description: t("transition.crossfade") },
    { value: "dissolve", label: "Dissolve", description: t("transition.dissolve") },
    { value: "wipe", label: "Wipe", description: t("transition.wipe") },
    { value: "slide", label: "Slide", description: t("transition.slide") },
    { value: "zoom", label: "Zoom", description: t("transition.zoom") },
    { value: "none", label: "None", description: t("transition.none") }
  ];
}

export default function TransitionEditor({
  fromSegment,
  toSegment,
  onSave,
  onClose
}) {
  // Get initial transition data
  const initialTransitionOut = fromSegment?.transition_out || { type: "crossfade", duration: 1.0 };
  const initialTransitionIn = toSegment?.transition_in || { type: "crossfade", duration: 0.5 };

  const [transitionOut, setTransitionOut] = useState(initialTransitionOut);
  const [transitionIn, setTransitionIn] = useState(initialTransitionIn);

  // Update when segments change
  useEffect(() => {
    setTransitionOut(fromSegment?.transition_out || { type: "crossfade", duration: 1.0 });
    setTransitionIn(toSegment?.transition_in || { type: "crossfade", duration: 0.5 });
  }, [fromSegment, toSegment]);

  const handleSave = () => {
    if (onSave) {
      onSave({
        fromSegmentId: fromSegment?.segment_id,
        toSegmentId: toSegment?.segment_id,
        transitionOut,
        transitionIn
      });
    }
  };

  // Get segment titles
  const fromTitle = typeof fromSegment?.prompt === "string"
    ? fromSegment.prompt
    : fromSegment?.prompt?.text || "Segment";

  const toTitle = typeof toSegment?.prompt === "string"
    ? toSegment.prompt
    : toSegment?.prompt?.text || "Segment";

  return (
    <div className="transition-editor">
      <div className="transition-editor-header">
        <h3>✂️ {t("transition.title")}</h3>
        <button
          type="button"
          className="transition-editor-close"
          onClick={onClose}
          title={t("common.close")}
        >
          ✕
        </button>
      </div>

      <div className="transition-editor-body">
        {/* From Segment Info */}
        <div className="transition-segment-info">
          <div className="transition-segment-label">{t("transition.fromSegment")}</div>
          <div className="transition-segment-title">
            {fromTitle.substring(0, 60)}
            {fromTitle.length > 60 ? "..." : ""}
          </div>
          {fromSegment?.end_image?.url && (
            <img
              src={fromSegment.end_image.url}
              alt="Image de fin"
              className="transition-segment-preview"
            />
          )}
        </div>

        {/* Transition Out (from first segment) */}
        <div className="transition-config-section">
          <h4>{t("transition.outLabel")}</h4>
          <p className="transition-config-description">
            {t("transition.outDesc")}
          </p>

          <div className="transition-form-group">
            <label>{t("transition.type")}</label>
            <select
              value={transitionOut.type}
              onChange={(e) => setTransitionOut({ ...transitionOut, type: e.target.value })}
              className="transition-select"
            >
              {getTransitionTypes().map(t => (
                <option key={t.value} value={t.value}>
                  {t.label} - {t.description}
                </option>
              ))}
            </select>
          </div>

          <div className="transition-form-group">
            <label>{t("transition.duration")}</label>
            <div className="transition-duration-input-group">
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.1"
                value={transitionOut.duration}
                onChange={(e) => setTransitionOut({ ...transitionOut, duration: parseFloat(e.target.value) })}
                className="transition-slider"
              />
              <input
                type="number"
                min="0.1"
                max="3.0"
                step="0.1"
                value={transitionOut.duration}
                onChange={(e) => setTransitionOut({ ...transitionOut, duration: parseFloat(e.target.value) || 1.0 })}
                className="transition-number-input"
              />
            </div>
          </div>
        </div>

        {/* Transition Arrow */}
        <div className="transition-arrow">
          <div className="transition-arrow-line" />
          <div className="transition-arrow-head">→</div>
        </div>

        {/* Transition In (to second segment) */}
        <div className="transition-config-section">
          <h4>{t("transition.inLabel")}</h4>
          <p className="transition-config-description">
            {t("transition.inDesc")}
          </p>

          <div className="transition-form-group">
            <label>{t("transition.type")}</label>
            <select
              value={transitionIn.type}
              onChange={(e) => setTransitionIn({ ...transitionIn, type: e.target.value })}
              className="transition-select"
            >
              {getTransitionTypes().map(t => (
                <option key={t.value} value={t.value}>
                  {t.label} - {t.description}
                </option>
              ))}
            </select>
          </div>

          <div className="transition-form-group">
            <label>{t("transition.duration")}</label>
            <div className="transition-duration-input-group">
              <input
                type="range"
                min="0.1"
                max="3.0"
                step="0.1"
                value={transitionIn.duration}
                onChange={(e) => setTransitionIn({ ...transitionIn, duration: parseFloat(e.target.value) })}
                className="transition-slider"
              />
              <input
                type="number"
                min="0.1"
                max="3.0"
                step="0.1"
                value={transitionIn.duration}
                onChange={(e) => setTransitionIn({ ...transitionIn, duration: parseFloat(e.target.value) || 0.5 })}
                className="transition-number-input"
              />
            </div>
          </div>
        </div>

        {/* To Segment Info */}
        <div className="transition-segment-info">
          <div className="transition-segment-label">{t("transition.toSegment")}</div>
          <div className="transition-segment-title">
            {toTitle.substring(0, 60)}
            {toTitle.length > 60 ? "..." : ""}
          </div>
          {toSegment?.start_image?.url && (
            <img
              src={toSegment.start_image.url}
              alt="Image de début"
              className="transition-segment-preview"
            />
          )}
        </div>
      </div>

      <div className="transition-editor-footer">
        <button
          type="button"
          className="transition-btn transition-btn-cancel"
          onClick={onClose}
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="transition-btn transition-btn-save"
          onClick={handleSave}
        >
          ✓ {t("common.save")}
        </button>
      </div>
    </div>
  );
}
